/**
 * Biometric authentication hook.
 * Wraps expo-local-authentication with preference persistence.
 */
import { useCallback } from 'react'
import * as LocalAuthentication from 'expo-local-authentication'
import { Platform } from 'react-native'
import { useTranslation } from 'react-i18next'
import { BiometricStore } from '../storage/secure-storage'
import { useAuthStore } from '../stores/auth.store'

const MAX_FAIL_COUNT = 3

export function useBiometric() {
  const { t } = useTranslation()
  const biometricFailCount = useAuthStore((s) => s.biometricFailCount)
  const incrementBiometricFail = useAuthStore((s) => s.incrementBiometricFail)
  const resetBiometricFail = useAuthStore((s) => s.resetBiometricFail)

  const isAvailable = useCallback(async (): Promise<boolean> => {
    if (Platform.OS === 'web') return false
    const hasHardware = await LocalAuthentication.hasHardwareAsync()
    const isEnrolled = await LocalAuthentication.isEnrolledAsync()
    return hasHardware && isEnrolled
  }, [])

  const isEnabled = useCallback(async (): Promise<boolean> => {
    return BiometricStore.isEnabled()
  }, [])

  const hasBeenPrompted = useCallback(async (): Promise<boolean> => {
    return BiometricStore.hasBeenPrompted()
  }, [])

  const authenticate = useCallback(async (): Promise<boolean> => {
    if (biometricFailCount >= MAX_FAIL_COUNT) return false

    const result = await LocalAuthentication.authenticateAsync({
      promptMessage: t('biometric.unlock'),
      cancelLabel: t('common.cancel'),
      disableDeviceFallback: true,
    })

    if (result.success) {
      resetBiometricFail()
      return true
    }

    incrementBiometricFail()
    return false
  }, [biometricFailCount, incrementBiometricFail, resetBiometricFail, t])

  const enable = useCallback(async (): Promise<void> => {
    await BiometricStore.setEnabled(true)
    await BiometricStore.setPrompted()
  }, [])

  const disable = useCallback(async (): Promise<void> => {
    await BiometricStore.setEnabled(false)
  }, [])

  const markPrompted = useCallback(async (): Promise<void> => {
    await BiometricStore.setPrompted()
  }, [])

  return {
    isAvailable,
    isEnabled,
    hasBeenPrompted,
    authenticate,
    enable,
    disable,
    markPrompted,
    shouldFallbackToPassword: biometricFailCount >= MAX_FAIL_COUNT,
  }
}
